import { Check } from 'lucide-react';
import { ArrowLink, Eyebrow, Reveal } from './Reveal';

const SERVICES = [
  {
    testid: 'service-card-talent',
    ctaTestid: 'service-cta-talent',
    index: '01 / RECRUITMENT',
    brand: 'Sania Talent Solutions',
    tagline: 'Right Talent. Right Role. Right Time.',
    image: 'https://images.pexels.com/photos/7792841/pexels-photo-7792841.jpeg?auto=compress&cs=tinysrgb&w=1200',
    alt: 'Indian recruitment professionals greeting with a handshake in an office',
    accent: { text: 'text-royal', bar: 'bg-royal', chip: 'bg-blue-50 text-royal' },
    features: [
      'Candidate sourcing and screening',
      'Interview and joining coordination',
      '7-Day commitment model',
      '30-Day commitment model',
      '6-Month commitment model',
      'Replacement support under agreed terms',
    ],
    cta: 'Request Talent',
  },
  {
    testid: 'service-card-oneday',
    ctaTestid: 'service-cta-oneday',
    index: '02 / FLEXIBLE STAFFING',
    brand: 'Sania One Day Hiring',
    tagline: 'The Workforce You Need. When You Need It.',
    image: 'https://images.unsplash.com/photo-1616734755909-bb016ce64930?q=80&w=1200&auto=format&fit=crop',
    alt: 'Indian banquet chef serving at a live catering counter',
    accent: { text: 'text-lime-deep', bar: 'bg-lime', chip: 'bg-lime-soft text-lime-deep' },
    features: [
      'One-day staffing',
      'Weekend and peak-demand teams',
      'Corporate catering support',
      'Events, exhibitions and retail staffing',
      'Deployment and attendance coordination',
    ],
    cta: 'Request Temporary Staff',
  },
  {
    testid: 'service-card-payroll',
    ctaTestid: 'service-cta-payroll',
    index: '03 / PAYROLL',
    brand: 'Sania Payroll Solutions',
    tagline: 'Simplifying Payroll. Supporting Your Workforce.',
    image: 'https://images.unsplash.com/photo-1762341116897-921e2a52f7ff?q=80&w=1200&auto=format&fit=crop',
    alt: 'Indian payroll professional working at her office desk',
    accent: { text: 'text-pine', bar: 'bg-pine', chip: 'bg-pine-soft text-pine' },
    features: [
      'Employee onboarding documentation',
      'Attendance and payroll processing',
      'Salary disbursement coordination',
      'PF and ESI support where applicable',
      'Workforce administration',
      'Contractual replacement coordination',
    ],
    cta: 'Discuss Payroll Solutions',
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="scroll-mt-28 bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <Eyebrow index="01">What we do</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-[2.75rem] lg:leading-[1.12]">
              Workforce solutions built around <span className="relative inline-block">your business.<span aria-hidden="true" className="absolute -bottom-1 left-0 h-2 w-full rounded-sm bg-lime/60" /></span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
              Whether you need one skilled professional, temporary operational staff or ongoing payroll support, our
              three priority services are designed to help you move forward.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.testid} delay={i * 0.12} className="h-full">
              <article
                data-testid={s.testid}
                className="group relative flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-slate-200/80 bg-white shadow-card transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-lift"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-1.5 ${s.accent.bar}`} />
                  <span className={`absolute left-4 top-5 rounded-full px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] ${s.accent.chip}`}>
                    {s.index}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-7">
                  <h3 className="font-display text-xl font-extrabold text-ink sm:text-2xl">{s.brand}</h3>
                  <p className={`mt-2 text-sm font-bold italic ${s.accent.text}`}>{s.tagline}</p>
                  <ul className="mt-5 space-y-2.5">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-[13.5px] leading-snug text-slate-600">
                        <span className={`mt-0.5 inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full ${s.accent.chip}`}>
                          <Check className="h-2.5 w-2.5" strokeWidth={3.2} aria-hidden="true" />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto pt-7">
                    <ArrowLink href="#contact" testid={s.ctaTestid} accent={s.accent.text}>
                      {s.cta}
                    </ArrowLink>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
